import {createAsyncThunk, createSelector, createSlice} from "@reduxjs/toolkit"
import axios from "axios"
import {selectTargetNote} from "./targetNoteSlice"
import {ensureWorkspaceTemplate} from "../utils/reduxUtilities"

export const fetchNoteRestrictions = createAsyncThunk(
	"noteRestrictionsSlice/fetchNoteRestrictions",
	async (workspaceId, {getState, rejectWithValue}) => {
		const state = getState()
		const userId = state.user?.value?.id

		if (!workspaceId) rejectWithValue("No valid workspace id.")
		try {
			const {data: existingRestrictions} = await axios.get(`/api/workspace/${workspaceId}/user/${userId}/restrictions`)
			return {workspaceId, existingRestrictions}
		} catch (error) {
			return rejectWithValue("Failed to load note restrictions")

		}

	}
)

const initialValue = {
	allowedNoteIds: [],
	allowedEditNoteIds: [],
	isLoading: false,
	error: null
}
const ensureWorkspace = (state, workspaceId) => ensureWorkspaceTemplate(state, workspaceId, initialValue)

export const noteRestrictionsSlice = createSlice({
	name: "noteRestrictionsSlice",
	initialState: {
		byWorkspace: { },
	},
	reducers: {
		updateAllowedNoteIds: (state, action) => {
			const {workspaceId, existingRestrictions} = action.payload
			const allowedNoteIds = []
			const allowedEditNoteIds = []
			for(let [noteId, type] of Object.entries(existingRestrictions)){
				allowedNoteIds.push(+noteId)
				if(type === "edit"){
					allowedEditNoteIds.push(+noteId)
				}
			}
			state.byWorkspace[workspaceId] = { allowedNoteIds, allowedEditNoteIds, isLoading: false, error: null }
		}
	},
	extraReducers: (builder) => {
		builder
			.addCase(fetchNoteRestrictions.pending, (state, action) => {
				const workspace = ensureWorkspace(state, action.meta.arg)
				workspace.isLoading = true
				workspace.error = null
			})
			.addCase(fetchNoteRestrictions.fulfilled, noteRestrictionsSlice.caseReducers.updateAllowedNoteIds)
			.addCase(fetchNoteRestrictions.rejected, (state, action) => {
				const workspace = ensureWorkspace(state, action.meta.arg)
				workspace.isLoading = false
				workspace.error = action.payload
			})
	}
})

export const { updateAllowedNoteIds } = noteRestrictionsSlice.actions


export const selectAllowedNoteIds = (state, workspaceId) => state.noteRestrictionsSlice.byWorkspace[workspaceId]?.allowedNoteIds ?? initialValue.allowedNoteIds
export const selectAllowedEditNoteIds = (state, workspaceId) => state.noteRestrictionsSlice.byWorkspace[workspaceId]?.allowedEditNoteIds ?? initialValue.allowedEditNoteIds
export const selectIsLoading = (state, workspaceId) => state.noteRestrictionsSlice.byWorkspace[workspaceId]?.isLoading ?? initialValue.isLoading

export const makeSelectNoteCanBeEdited = (workspaceId) => createSelector(
	[
		(state) => selectAllowedNoteIds(state, workspaceId),
		(state) => selectAllowedEditNoteIds(state, workspaceId),
		(state) => selectTargetNote(state, workspaceId),
		(state) => selectIsLoading(state, workspaceId)
	],
	(readNoteIds, editNoteIds, targetNote) => {
		if(targetNote.id === null){
			if(process.env.NODE_ENV !== "production") {
				console.warn("🚨🚨🚨 \n    makeSelectNoteCanBeEdited factory encountered a null targetNote.id value, and is returning as editable \n🚨🚨🚨")
			}
			return true
		}
		if(editNoteIds.length === 0 && readNoteIds.length === 0) {
			//user has global write permission
			return true
		}
		if(targetNote.parentId === null) {
			//target note is root level
			return editNoteIds.includes(targetNote.id)
		} else {
			// target note is below root level, find root-level parent and check that permission
			const rootLevelParentId = targetNote.path.split(".")[0]
			return editNoteIds.includes(+rootLevelParentId)
		}
	}
)

export default noteRestrictionsSlice.reducer